import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../hooks/useChat';
import { ToolCard } from './ToolCard';
import { CodeBlockActions } from './CodeBlockActions';

/**
 * Stage 28: does a fenced block look like it contains Markdown content?
 * Triggers the Copy / Save-as-.md floating buttons. Covers the two common
 * cases: explicit ```markdown / ```md language tags, and no-tag fences
 * whose bodies open with a Markdown heading or an HTML comment preamble
 * (which our TINS skill headers use).
 */
function looksLikeMarkdown(lang: string, body: string): boolean {
  const l = lang.toLowerCase();
  if (l === 'markdown' || l === 'md') return true;
  if (lang !== '') return false;
  const trimmed = body.trimStart();
  return (
    /^#{1,6}\s/.test(trimmed) ||
    /^<!--\s*TINS/i.test(trimmed) ||
    /^\[TOC\]/.test(trimmed)
  );
}

/**
 * Minimal, safe markdown-ish renderer:
 * - Splits on blank lines → paragraphs
 * - Detects triple-backtick fenced code blocks
 * - Renders everything else as plain text (React already escapes)
 */
function renderBody(text: string): JSX.Element[] {
  const blocks: JSX.Element[] = [];
  let remaining = text;
  let key = 0;
  const fenceRe = /```([a-zA-Z0-9_+-]*)\n([\s\S]*?)```/;

  while (remaining.length > 0) {
    const m = remaining.match(fenceRe);
    if (!m || m.index === undefined) {
      pushText(blocks, remaining, key++);
      break;
    }
    if (m.index > 0) {
      pushText(blocks, remaining.slice(0, m.index), key++);
    }
    const lang = m[1] || '';
    const code = m[2] ?? '';
    blocks.push(
      <pre
        key={`code-${key++}`}
        className="code-block"
        data-lang={lang}
        style={{ position: 'relative' }}
      >
        <code>{code}</code>
        {looksLikeMarkdown(lang, code) ? (
          <CodeBlockActions body={code} />
        ) : null}
      </pre>,
    );
    remaining = remaining.slice(m.index + m[0].length);
  }

  return blocks;
}

function pushText(blocks: JSX.Element[], text: string, keyBase: number): void {
  const paragraphs = text.split(/\n{2,}/);
  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i];
    if (!p.trim()) continue;
    // Preserve single newlines inside a paragraph as <br>.
    const lines = p.split('\n');
    blocks.push(
      <p key={`p-${keyBase}-${i}`}>
        {lines.map((line, li) => (
          <span key={li}>
            {line}
            {li < lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>,
    );
  }
}

interface ChatTranscriptProps {
  messages: ChatMessage[];
  /** Stage 12 (C2): when present, each assistant bubble shows a Fork button
   *  that branches the conversation at that turn. `turnIndex` is the 0-based
   *  position within the loaded messages array. */
  onFork?: (turnIndex: number) => void;
}

export function ChatTranscript({ messages, onFork }: ChatTranscriptProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="chat-transcript chat-empty" ref={ref}>
        <div className="chat-empty-hint muted">
          Start the conversation — type a message below.
        </div>
      </div>
    );
  }

  return (
    <div className="chat-transcript" ref={ref}>
      {messages.map((m, i) => (
        <div
          key={m.id}
          className={`turn turn-${m.role}${m.streaming ? ' turn-streaming' : ''}${m.error ? ' turn-error' : ''}`}
        >
          <div className="turn-role">
            {m.role === 'user' ? 'you' : 'assistant'}
          </div>
          <div className="turn-body">
            {m.attachment ? (
              <img
                src={m.attachment.signedUrl}
                alt="Attached image"
                className="turn-attachment-image"
              />
            ) : null}
            {m.text ? (
              renderBody(m.text)
            ) : m.streaming && (m.toolOutputs?.length ?? 0) === 0 ? (
              <span className="muted">…</span>
            ) : null}
            {m.streaming && m.text ? (
              <span className="turn-cursor" aria-hidden="true">
                ▌
              </span>
            ) : null}
          </div>
          {m.toolOutputs && m.toolOutputs.length > 0 ? (
            <div className="turn-tools">
              {m.toolOutputs.map((to) => (
                <ToolCard key={to.id} output={to} />
              ))}
            </div>
          ) : null}
          {m.grounding && m.grounding.citations.length > 0 ? (
            <div className="grounding-card">
              <div className="grounding-label">
                Web search{m.grounding.query ? `: ${m.grounding.query}` : ''}
              </div>
              <ol className="grounding-citations">
                {m.grounding.citations.map((c, i) => (
                  <li key={i}>
                    <a href={c.uri} target="_blank" rel="noopener noreferrer">
                      {c.title || c.domain || c.uri}
                    </a>
                    {c.domain ? (
                      <span className="muted small"> · {c.domain}</span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          <div className="turn-footer">
            {m.koji ? <span className="turn-koji">{m.koji}</span> : null}
            {m.mood ? (
              <span
                className={
                  'mood-chip' +
                  (m.mood.signalSource === 'model' ? ' mood-chip-shifted' : '')
                }
                title={
                  `(${m.mood.valence.toFixed(2)}, ${m.mood.arousal.toFixed(2)}) ` +
                  (m.mood.alpha != null ? `α=${m.mood.alpha.toFixed(2)} ` : '') +
                  `| ${
                    m.mood.signalSource === 'model'
                      ? `signal: model (${m.mood.signal?.v.toFixed(2) ?? '?'}, ${m.mood.signal?.a.toFixed(2) ?? '?'})`
                      : m.mood.signalSource === 'held'
                        ? 'signal: held (tag missing)'
                        : 'signal: baseline'
                  }`
                }
              >
                mood: {m.mood.label.toLowerCase()}
              </span>
            ) : null}
            {m.speechText ? (
              <span
                className="turn-koji"
                title={`Spoken summary: ${m.speechText}`}
                aria-label="Spoken summary"
              >
                🔊 {m.speechText.length} chars
              </span>
            ) : null}
            {onFork && m.role === 'assistant' && !m.streaming ? (
              <button
                type="button"
                className="turn-fork"
                onClick={() => onFork(i)}
                title="Fork the conversation from this turn"
                aria-label="Fork conversation here"
              >
                ⑂ fork
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
